import { Items } from '../Card';
import produce from 'immer';
import React, { useEffect, useState, useContext } from 'react';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';

import { Page } from '.';
import { PagesQuery, useReorderPageItemsMutation } from '../../generated/graphql';
import { reorder } from '../../helpers/reorder';
import Item from '../Item';
import { pagesQuery } from '../LeftContainer/graphql';
import styles from './index.scss';
import { TrackingContext } from '../../TrackingContext';
import Honeybadger from 'honeybadger-js';
import CVAlert from '../CVAlert';

interface Props {
  page: Page;
}

const PageCardItems: React.FC<Props> = ({ page }) => {
  const [sortedItems, setSortedItems] = useState(page.items || []);
  const [reorderPageItems] = useReorderPageItemsMutation();
  const { track } = useContext(TrackingContext);
  const [erroring, setErroring] = useState(false);

  useEffect(() => {
    setSortedItems(page.items || []);
  }, [page.items]);

  const onDragEnd = async ({ source, destination }: DropResult) => {
    const sorted = reorder(sortedItems, source.index, destination?.index);
    setSortedItems(sorted);

    const updated = sorted.map(({ id, rank }: any) => ({
      id,
      rank,
    }));

    try {
      await reorderPageItems({
        variables: {
          attributes: {
            pageId: page.id,
            pageItems: updated,
          },
        },
        update(cache, { data }) {
          const { pages } = cache.readQuery<PagesQuery>({ query: pagesQuery })!;

          const nextState = produce(pages, draft => {
            const pg = draft.find(i => i.id === page.id);

            if (pg) {
              pg.items = data?.reorderPageItems!;
            }
          });

          track('WWS Page Edited', {
            pageType: page.type,
            action: 'edit',
            actionName: 'reorder items',
            pageVisible: page.show,
          });
          cache.writeQuery({ query: pagesQuery, data: { pages: nextState } });
        },
      });
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error reordering page cards',
        context: {
          error,
        },
      });
      setSortedItems(page?.items || []);
    }
  };

  return (
    <>
      <CVAlert erroring={erroring} onTimeout={() => setErroring(false)} />
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`${page.id}-${page.title}-droppable-1`} type={`PAGE_ITEM_${page.type}`}>
          {provided => (
            <Items
              innerRef={provided.innerRef}
              className={`${styles.draggablePlaceholder} ${page.type === 'WeddingPage' ? styles.noBorder : ''}`}
              {...provided.droppableProps}
            >
              {sortedItems.map((item, i) => (
                <Item key={`${item.type}-${item.id}`} item={item} page={page} index={i} />
              ))}
              {provided.placeholder}
            </Items>
          )}
        </Droppable>
      </DragDropContext>
    </>
  );
};

export default PageCardItems;
